import { ExternalEventResponseState } from '@griply/types';
import { focusOutline } from 'shared/assets/styles';
import { typographyToCss } from 'shared/lib/mui';
import styled, { css } from 'styled-components';

export const Container = styled.div<{
  $show: boolean;
  $items: number;
  $spaceAbove: boolean;
}>`
  height: ${({ theme, $items, $show }) =>
    $show ? `calc(${$items * 1.8}rem + ${theme.spacing(1)})` : 0};

  border-radius: ${({ theme }) => theme.spacing(2)};
  background: ${({ theme }) => theme.palette.grey[50]};
  overflow: hidden;
  transition: height 200ms;

  ${({ $show, $spaceAbove }) =>
    $show &&
    $spaceAbove &&
    css`
      margin-top: ${({ theme }) => theme.spacing(6)};
    `};
`;

/* Shared tracks: the time column fits the longest label, so titles align. */
export const List = styled.ul`
  display: grid;
  grid-template-columns: max-content max-content minmax(0, max-content);
  justify-content: start;
  column-gap: ${({ theme }) => theme.spacing(2)};

  list-style: none;

  margin: 0;
  padding: ${({ theme }) => `${theme.spacing(0.5)} 0`};
`;

export const ListItem = styled.li`
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
`;

export const EventContainer = styled.a<{ $isPast?: boolean }>`
  display: grid;
  grid-column: 1 / -1;
  grid-template-columns: subgrid;
  align-items: center;

  min-width: 0;
  padding: ${({ theme }) => `0 ${theme.spacing(1.5)}`};
  border: none;
  background: transparent;
  text-align: left;
  cursor: pointer;

  text-decoration: none;
  color: ${({ theme }) => theme.palette.text.secondary};

  &:focus-visible {
    ${focusOutline};
  }

  ${({ $isPast }) =>
    $isPast &&
    css`
      opacity: 0.6;
    `};
`;

export const Color = styled.span<{
  $color?: string;
  $responseState?: ExternalEventResponseState;
}>`
  display: block;
  height: 1rem;
  width: 0.3rem;

  border-radius: ${({ theme }) => theme.spacing(0.5)};
  background: ${({ $color }) => $color ?? 'transparent'};

  ${({ $responseState, $color }) =>
    $responseState === ExternalEventResponseState.AwaitingResponse &&
    css`
      background: transparent;
      box-shadow: inset 0 0 0 1px ${$color ?? 'transparent'};
    `};

  ${({ $responseState, $color }) =>
    $responseState === ExternalEventResponseState.Tentative &&
    css`
      /* No radius here: rounded caps would eat the top and bottom line of a 10px bar. */
      border-radius: 0;
      background: repeating-linear-gradient(
        to bottom,
        ${$color ?? 'transparent'} 0 0.2rem,
        transparent 0.2rem 0.35rem
      );
    `};
`;

export const Time = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.overline)};

  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Title = styled.span`
  ${({ theme }) => typographyToCss(theme.typography.overline)};

  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
  color: ${({ theme }) => theme.palette.text.primary};
`;
